import {
  getQrImageSource,
  getTotpFactors,
  getTotpSetupPlan,
  isAal2,
  isExplicitTotpFactor,
  isValidTotpCode,
  selectVerifiedTotpFactor,
  type MfaFactorList,
} from "./customerMfa";

export const genericAdminStaffMfaError =
  "Authenticator verification could not be completed.";

export const canonicalAdminStaffTotpName = "Vintera Health staff";

export type AdminStaffTotpFactorState =
  | "no_factor"
  | "unverified_factor"
  | "verified_factor";

export function classifyAdminStaffTotpFactors(
  factors: MfaFactorList,
): AdminStaffTotpFactorState {
  const setupPlan = getTotpSetupPlan(factors);
  if (setupPlan.action === "use_verified_factor") return "verified_factor";
  if (setupPlan.action === "replace_unverified_factors") {
    return "unverified_factor";
  }
  return "no_factor";
}

export function resolveAdminStaffRecoveryEntryRoute({
  currentLevel,
  factorState,
}: {
  currentLevel: string | null;
  factorState: AdminStaffTotpFactorState;
}) {
  if (currentLevel !== "aal1" && !isAal2(currentLevel)) return null;
  return factorState === "verified_factor" && !isAal2(currentLevel)
    ? ("/admin/mfa/verify" as const)
    : ("/admin/reset-password" as const);
}

export function resolveAdminStaffPostRecoveryPasswordRoute(
  factorState: AdminStaffTotpFactorState,
) {
  return factorState === "verified_factor"
    ? ("/admin/login?passwordReset=success" as const)
    : ("/admin/mfa/setup" as const);
}

export const recognizedAdminStaffMfaErrorCodes = new Set([
  "mfa_challenge_expired",
  "mfa_factor_name_conflict",
  "mfa_factor_not_found",
  "mfa_ip_address_mismatch",
  "mfa_totp_enroll_not_enabled",
  "mfa_totp_verify_not_enabled",
  "mfa_verification_failed",
  "no_authorization",
  "over_request_rate_limit",
]);

type AdminStaffMfaError = {
  code?: string;
  message?: string;
  name?: string;
  status?: number;
};

type AdminStaffMfaEnrollment = {
  id?: string;
  type?: string;
  totp?: {
    qr_code?: string;
    secret?: string;
  };
};

export type AdminStaffMfaBrowserClient = {
  auth: {
    mfa: {
      getAuthenticatorAssuranceLevel(): Promise<{
        data: { currentLevel: string | null; nextLevel?: string | null } | null;
        error: AdminStaffMfaError | null;
      }>;
      listFactors(): Promise<{
        data: MfaFactorList;
        error: AdminStaffMfaError | null;
      }>;
      enroll(params: {
        factorType: "totp";
        friendlyName: string;
      }): Promise<{
        data: AdminStaffMfaEnrollment | null;
        error: AdminStaffMfaError | null;
      }>;
      unenroll(params: { factorId: string }): Promise<{
        data?: unknown;
        error: AdminStaffMfaError | null;
      }>;
      challenge(params: { factorId: string }): Promise<{
        data: { id?: string } | null;
        error: AdminStaffMfaError | null;
      }>;
      verify(params: {
        factorId: string;
        challengeId: string;
        code: string;
      }): Promise<{
        data?: unknown;
        error: AdminStaffMfaError | null;
      }>;
    };
  };
};

export type AdminStaffMfaOperation =
  | "listFactors"
  | "unenroll"
  | "enroll"
  | "challenge"
  | "verify"
  | "getAuthenticatorAssuranceLevel";

type AdminStaffMfaFailure = {
  ok: false;
  error: typeof genericAdminStaffMfaError;
  operation: AdminStaffMfaOperation;
};

function failure(operation: AdminStaffMfaOperation): AdminStaffMfaFailure {
  return { ok: false, error: genericAdminStaffMfaError, operation };
}

export function getRecognizedAdminStaffMfaErrorCode(error: unknown) {
  if (!error || typeof error !== "object") return null;

  const code = (error as AdminStaffMfaError).code;
  return typeof code === "string" && recognizedAdminStaffMfaErrorCodes.has(code)
    ? code
    : null;
}

export async function prepareAdminStaffMfaEnrollment(
  client: AdminStaffMfaBrowserClient
) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const factorsResult = await client.auth.mfa.listFactors();
    if (factorsResult.error) return failure("listFactors");

    const setupPlan = getTotpSetupPlan(factorsResult.data);
    if (setupPlan.action === "use_verified_factor") {
      return {
        ok: true as const,
        state: "verified_factor_exists" as const,
        redirectTo: "/admin/mfa/verify" as const,
      };
    }

    for (const factorId of setupPlan.unverifiedFactorIds) {
      const unenrollResult = await client.auth.mfa.unenroll({ factorId });
      const errorCode = getRecognizedAdminStaffMfaErrorCode(
        unenrollResult.error
      );

      if (unenrollResult.error && errorCode !== "mfa_factor_not_found") {
        return failure("unenroll");
      }
    }

    const enrollResult = await client.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: canonicalAdminStaffTotpName,
    });
    const enrollment = enrollResult.data;

    if (enrollResult.error) {
      if (
        attempt === 0 &&
        getRecognizedAdminStaffMfaErrorCode(enrollResult.error) ===
          "mfa_factor_name_conflict"
      ) {
        continue;
      }

      return failure("enroll");
    }

    if (
      !enrollment?.id ||
      enrollment.type !== "totp" ||
      !enrollment.totp?.qr_code ||
      !enrollment.totp.secret
    ) {
      return failure("enroll");
    }

    return {
      ok: true as const,
      state: "ready" as const,
      factorId: enrollment.id,
      qrCodeSource: getQrImageSource(enrollment.totp.qr_code),
      secret: enrollment.totp.secret,
    };
  }

  return failure("enroll");
}

async function verifyOwnedAdminStaffMfaFactor({
  client,
  code,
  factorId,
}: {
  client: AdminStaffMfaBrowserClient;
  code: unknown;
  factorId: string;
}) {
  if (typeof code !== "string" || !isValidTotpCode(code)) {
    return failure("verify");
  }

  const challengeResult = await client.auth.mfa.challenge({ factorId });
  const challengeId = challengeResult.data?.id;
  if (challengeResult.error || !challengeId) return failure("challenge");

  const verifyResult = await client.auth.mfa.verify({
    factorId,
    challengeId,
    code: code.trim(),
  });
  if (verifyResult.error) return failure("verify");

  const aalResult = await client.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalResult.error || !isAal2(aalResult.data?.currentLevel || null)) {
    return failure("getAuthenticatorAssuranceLevel");
  }

  return {
    ok: true as const,
    redirectTo: "/admin" as const,
    verifiedFactorId: factorId,
  };
}

export async function verifyAdminStaffMfaEnrollment({
  client,
  code,
  factorId,
}: {
  client: AdminStaffMfaBrowserClient;
  code: unknown;
  factorId: string;
}) {
  const factorsResult = await client.auth.mfa.listFactors();
  if (factorsResult.error) return failure("listFactors");
  const factors = Array.isArray(factorsResult.data)
    ? factorsResult.data
    : factorsResult.data?.all || factorsResult.data?.totp || [];
  const factor = factors.find((candidate) => candidate.id === factorId
    && candidate.status !== "verified" && isExplicitTotpFactor(candidate));
  if (!factor) return failure("listFactors");
  return verifyOwnedAdminStaffMfaFactor({ client, code, factorId });
}

export async function verifyAdminStaffMfaLogin({
  client,
  code,
}: {
  client: AdminStaffMfaBrowserClient;
  code: unknown;
}) {
  if (typeof code !== "string" || !isValidTotpCode(code)) {
    return failure("verify");
  }

  const factorsResult = await client.auth.mfa.listFactors();
  if (factorsResult.error) return failure("listFactors");

  const factor = selectVerifiedTotpFactor(factorsResult.data);
  if (!factor) return failure("listFactors");

  return verifyOwnedAdminStaffMfaFactor({
    client,
    code,
    factorId: factor.id,
  });
}

export async function verifyAdminStaffMfaStepUp({
  client,
  code,
}: {
  client: AdminStaffMfaBrowserClient;
  code: unknown;
}) {
  if (typeof code !== "string" || !isValidTotpCode(code)) {
    return failure("verify");
  }

  const factorsResult = await client.auth.mfa.listFactors();
  if (factorsResult.error) return failure("listFactors");

  const verifiedFactors = getTotpFactors(factorsResult.data).filter(
    (factor) => factor.status === "verified" && isExplicitTotpFactor(factor),
  );
  if (verifiedFactors.length !== 1) return failure("listFactors");

  return verifyOwnedAdminStaffMfaFactor({
    client,
    code,
    factorId: verifiedFactors[0].id,
  });
}
